// A posted payout's report (aldoo-na store/payouts/invoices,
// payouts_invoice_file) in the framework's PDF viewer, in a modal — downloadable
// from the viewer's toolbar. The vendor's Payouts, the admin's vendor page and
// Admin → Vendors → Payouts open it.
import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import usePdfModal from "./usePdfModal"
import PDFViewer from "aldoo-ra/PDFViewer"
import { PayoutsInvoiceFile } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import { viewerTexts } from "./ContractPdf"
import { pdfBytes, downloadPdf } from "./pdf"

// `height`: the viewer's (the modal's 75vh, a payout's card a shorter one)
export function InvoiceViewer({ payoutId, t, height = "75vh" }) {
  const [answer, setAnswer] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let gone = false
    setAnswer(null)
    setFailed(false)
    PayoutsInvoiceFile(payoutId)
      .then((res) => {
        if (gone) return
        if (res?.pdf) setAnswer(res)
        else setFailed(true)
      })
      .catch(() => !gone && setFailed(true))
    return () => {
      gone = true
    }
  }, [payoutId])
  const bytes = useMemo(() => pdfBytes(answer), [answer])
  const texts = useMemo(() => ({ ...viewerTexts(t), error: t`Could not load the report` }), [t])

  if (failed) return <div className="w-full rounded-md border border-admin_bordered bg-admin_neutral_1 p-6 text-sm text-admin_text_secondary">{t`Could not load the report`}</div>
  return (
    <div className="w-full" data-testid="invoice-preview">
      <PDFViewer src={bytes} height={height} onDownload={answer ? () => downloadPdf(answer) : undefined} texts={texts} />
    </div>
  )
}

/** `payout`: the payout whose invoice is open (null: closed). */
export default function InvoicePdf({ payout, onClose }) {
  const t = usePortalTexts(PAYOUTS)
  const pdfModal = usePdfModal()
  return (
    <Modal
      isOpen={!!payout}
      onClose={onClose}
      title={payout ? t("Payout report No. %s", payout.invoiceNumber) : ""}
      variant={pdfModal.variant}
      closeOnEscape
      closeOnClickOutside
      styleOverrides={pdfModal.styleOverrides}
    >
      {payout && <InvoiceViewer payoutId={payout._id} t={t} height={pdfModal.height} />}
    </Modal>
  )
}

/**
 * A payout's documents, beside it in a list: its invoice (opened in the
 * viewer), Stripe's receipt (a new tab; Stripe keeps it 60 days) and the
 * bank's trace id. Nothing when it has none of them.
 */
/**
 * Stripe's receipt: the copy kept as a PDF (its link expires after 60 days), else
 * Stripe's page while no copy is kept yet. The tab is opened at once (a popup
 * blocker lets a click open one), then given the PDF.
 */
export async function openReceipt(payout) {
  const tab = window.open("", "_blank")
  const answer = await PayoutsInvoiceFile(payout._id, "receipt").catch(() => null)
  const bytes = answer?.pdf ? pdfBytes(answer) : null
  const url = bytes ? URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })) : payout.receiptUrl
  if (tab) tab.location.href = url
  else window.open(url, "_blank", "noopener,noreferrer")
}

export function PayoutDocuments({ payout, onOpenInvoice, t }) {
  if (!payout.invoiceNumber && !payout.receiptUrl && !payout.traceId) return null
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 w-full text-xs" data-testid="payout-documents">
      {payout.invoiceNumber && (
        <button type="button" className="font-medium text-admin_primary hover:underline" onClick={() => onOpenInvoice(payout)} data-testid="payout-invoice">
          {t`Payout report`} {payout.invoiceNumber}
        </button>
      )}
      {payout.receiptUrl && (
        <button type="button" className="font-medium text-admin_primary hover:underline" onClick={() => openReceipt(payout)} data-testid="payout-receipt">
          {t`Stripe receipt`} ↗
        </button>
      )}
      {payout.traceId && <span className="text-admin_text_secondary tabular-nums" data-testid="payout-trace">{t("Trace ID %s", payout.traceId)}</span>}
    </span>
  )
}
